import { onMounted, onUnmounted, ref, watch, type Ref } from 'vue'

/**
 * Tracks which section is currently in the viewport as the user scrolls.
 *
 * Uses IntersectionObserver to mark sections active, plus a scroll listener
 * to force the last section active at the bottom of the page (handles sections
 * too short to ever reach the active zone at the top of the viewport).
 *
 * Must be called inside a Vue component's setup(). The observer is initialized
 * in onMounted so it never runs during SSR.
 *
 * @param sections - Ref to an array of section HTMLElements (populated by template refs)
 * @param options.headerOffset - Height of the sticky header in px; shifts the active zone down
 */
export function useScrollSpy(
  sections: Ref<HTMLElement[]>,
  options?: { headerOffset?: number },
): { activeSectionId: Ref<string | null> } {
  const activeSectionId = ref<string | null>(null)

  let observer: IntersectionObserver | null = null
  let scrollListener: (() => void) | null = null
  const intersectingIds = new Set<string>()

  // Pick the topmost currently-intersecting section (in document order)
  function pickActive() {
    for (const el of sections.value) {
      if (el?.id && intersectingIds.has(el.id)) {
        activeSectionId.value = el.id
        return
      }
    }
  }

  onMounted(() => {
    if (typeof IntersectionObserver === 'undefined') return

    const offset = options?.headerOffset ?? 0

    // Active zone: below the sticky header, above the middle of the viewport.
    // A section becomes "active" when its top crosses into this zone.
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).id
          if (entry.isIntersecting) {
            intersectingIds.add(id)
          } else {
            intersectingIds.delete(id)
          }
        }
        pickActive()
      },
      { rootMargin: `-${offset}px 0px -50% 0px` },
    )

    sections.value.forEach((el) => el && observer!.observe(el))

    // Bottom-of-page edge case: when the last section is shorter than the active
    // zone, it will never intersect. Force it active on scroll-to-bottom.
    scrollListener = () => {
      if (window.scrollY + window.innerHeight >= document.body.scrollHeight - 10) {
        const last = sections.value[sections.value.length - 1]
        if (last?.id) activeSectionId.value = last.id
      }
    }

    window.addEventListener('scroll', scrollListener, { passive: true })
  })

  // Update the URL hash whenever the active section changes.
  // Uses replaceState so scrolling does not pollute the browser history stack.
  watch(activeSectionId, (id) => {
    if (id) history.replaceState(null, '', `#${id}`)
  })

  onUnmounted(() => {
    observer?.disconnect()
    observer = null
    intersectingIds.clear()
    if (scrollListener) {
      window.removeEventListener('scroll', scrollListener)
      scrollListener = null
    }
  })

  return { activeSectionId }
}
